// @flow
import React, { useState, useEffect } from 'react';
import Debug from 'debug';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import DeleteIcon from '@material-ui/icons/Delete';
import type { Config as ConfigType, TISMessage } from '../../../types';
import Config from '../../../models/Config';
import ConfirmDialog from '../../ui/ConfirmDialog';
import ConfigItem from '../../Options/Config/ConfigItem';
import Item from './Item';
import Player from './Player';
import SaveDialog from './SaveDialog';

const debug = Debug('amms:modules:tisPlayer:generator');

const EMPTY_PRESET = {
    title: 'Unsaved'
};
const EMPTY_ITEM = {
    enabled: true,
    type: 'car',
    interval: 1000,
    delay: 0,
    generatorType: 'relative',
    baseCoordinates: [50.10715311393142, 14.263167288154364],
    stepMultiplier: 0.0005
};

const useStyles = makeStyles(theme => ({
    content: {
        paddingTop: theme.spacing(2),
        overflowY: 'auto'
    },
    presetSelect: {
        minWidth: 200
    },
    deleteButton: {
        color: theme.palette.error.dark,
        marginLeft: theme.spacing(4)
    }
}));

type TisPlayerConfig = $PropertyType<ConfigType, 'tisPlayer'>;

type Props = $ReadOnly<{
    playerRef: {},
    connected: boolean,
    config: TisPlayerConfig,

    onConfigChange: (config: TisPlayerConfig) => void,
    playMessage: (message: TISMessage) => void,
    onPlay: () => void,
    onStop: () => void,
}>;
function Generator({ playerRef, connected, config, onConfigChange, playMessage, onPlay, onStop }: Props) {
    const classes = useStyles();
    const [preset, setPreset] = useState();
    const [saveDialogVisible, setSaveDialogVisible] = useState(false);
    const generatorConfig = config && config.generator;
    const currentPresetName = generatorConfig && generatorConfig.currentPreset || 'unsaved';
    const presets = generatorConfig && generatorConfig.presets;

    useEffect(() => {
        if(!currentPresetName || !presets || !presets[currentPresetName]) {
            setPreset(EMPTY_PRESET);
        }
        else {
            setPreset(presets[currentPresetName]);
        }
    }, [presets, currentPresetName]);

    const getPlayerRef = (ref) => {
        playerRef(ref);
    };

    const handlePresetChange = (e) => {
        const preset = presets[e.target.value] || EMPTY_PRESET;

        Config.findByIdAndUpdate('tisPlayer', {
            ...config,
            generator: {
                ...generatorConfig,
                currentPreset: e.target.value,
                presets: {
                    ...presets,
                    unsaved: preset
                }
            }
        }, { upsert: true });
    };

    const addItem = () => {
        onConfigChange({
            ...config,
            generator: {
                ...generatorConfig,
                presets: {
                    ...presets,
                    [currentPresetName]: {
                        ...preset,
                        items: [
                            ...(preset.items || []),
                            EMPTY_ITEM
                        ]
                    }
                }
            }
        });
    };

    const showSaveDialog = () => setSaveDialogVisible(true);
    const hideSaveDialog = () => setSaveDialogVisible(false);
    const handleSavePreset = (preset) => {
        const key = preset.title && preset.title.toLowerCase();

        if(!key) {
            throw new Error('Unvalid preset key');
        }

        const newConfig = {
            ...config,
            generator: {
                ...generatorConfig,
                currentPreset: key,
                presets: {
                    ...presets,
                    [key]: preset
                }
            }
        };

        Config.findByIdAndUpdate('tisPlayer', newConfig, { upsert: true });
        hideSaveDialog();
    };
    const handleDeletePreset = () => {
        const presets = {
            ...generatorConfig.presets
        };

        delete presets[currentPresetName];

        const newConfig = {
            ...config,
            generator: {
                ...generatorConfig,
                currentPreset: 'unsaved',
                presets
            }
        };

        Config.findByIdAndUpdate('tisPlayer', newConfig, { upsert: true });
    };

    const handleItemChange = (path, value, newConfig) => {
        if(currentPresetName !== 'unsaved') {
            Config.findByIdAndUpdate('tisPlayer', {
                ...newConfig,
                generator: {
                    ...newConfig.generator,
                    currentPreset: 'unsaved'
                }
            }, { upsert: true });
        }
    };

    return (
        <div className={classes.content}>
            <div>
                <FormControl variant="outlined" className={classes.presetSelect}>
                    <InputLabel>
                        Preset
                    </InputLabel>
                    <Select
                        value={currentPresetName}
                        onChange={handlePresetChange}
                    >
                        <MenuItem value="unsaved">
                            <em>Unsaved</em>
                        </MenuItem>
                        {presets && Object.keys(presets).filter(name => name !== 'unsaved').map(presetName => {
                            return (
                                <MenuItem key={presetName} value={presetName}>{presets[presetName].title}</MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                <Button variant="outlined" color="primary" onClick={showSaveDialog}>
                    <FormattedMessage
                        id="tisPlayer.generator.saveAs"
                        defaultMessage="Save as"
                    />
                </Button>

                <ConfirmDialog
                    title={(
                        <FormattedMessage
                            id="tisPlayer.generator.deletePreset.title"
                            defaultMessage="Are you sure you want to delete this preset?"
                        />
                    )}
                    onConfirm={handleDeletePreset}
                >
                    {(openDialog) => (
                        <IconButton size="small" className={classes.deleteButton} onClick={openDialog}>
                            <DeleteIcon />
                        </IconButton>
                    )}
                </ConfirmDialog>

                {preset && <SaveDialog open={saveDialogVisible} preset={preset} onClose={hideSaveDialog} onSave={handleSavePreset} />}
            </div>

            <ConfigItem
                type="boolean"
                label={(
                    <FormattedMessage
                        id="tisPlayer.autoplay"
                        defaultMessage="Autoplay"
                    />
                )}
                path={['tisPlayer', 'autoplay']}
            />

            {preset &&
                <React.Fragment>
                    {connected &&
                        <Player
                            playerRef={getPlayerRef}
                            items={preset.items}
                            playMessage={playMessage}
                            onStart={onPlay}
                            onStop={onStop}
                        />
                    }

                    <div>
                        <h4>
                            <FormattedMessage
                                id="tisPlayer.generator.items"
                                defaultMessage="items"
                            />
                        </h4>
                        {preset.items && preset.items.map((item, index) => (
                            <Item
                                key={index}
                                item={item}
                                path={['tisPlayer', 'generator', 'presets', 'unsaved', 'items', index]}
                                onChange={handleItemChange}
                            />
                        ))}
                    </div>

                    <Button variant="outlined" color="primary" onClick={addItem}>
                        <FormattedMessage
                            id="tisPlayer.generator.add"
                            defaultMessage="Add"
                        />
                    </Button>
                </React.Fragment>
            }
        </div>
    );
}

export default Generator;